import React, { useCallback, useEffect, useMemo } from 'react';
import { StyledDataGrid } from '../UserRequestsStyles';
import { buildRegistrationColumns } from './columns';
import { ToolbarPropsOverrides } from '@mui/x-data-grid';
import UserRequestsToolbar from '../UserRequestsToolbar';
import { useUserRequestsPermissions } from '../hooks';
import {
  useRegistration,
  useUpdateUserRequestStatus,
} from '../hooks/useRegistration';
import { RegistrationRequestStatusEnum } from '../types';
import { usePagination } from '../../../shared/hooks/usePagination';
import { useUserDateFormatter } from '../hooks';
import { useSnackbar } from 'notistack';
import { getApiErrorMessage } from '../../../shared/api/getApiErrorMessage';
import { useNavigate } from 'react-router-dom';

const RegistrationRequests = () => {
  const navigate = useNavigate();
  const { mutateAsync: updateUserRequestStatus } = useUpdateUserRequestStatus();
  const { formatDateTime: formatDate } = useUserDateFormatter();
  const { enqueueSnackbar } = useSnackbar();

  const {
    pageIndex,
    pageSize,
    paginationModel,
    filterModel,
    sortModel,
    filters,
    sortField,
    sortOrder,
    handleFilterModelChange,
    handlePagination,
    handleSortModelChange,
  } = usePagination({
    initialPageSize: 10,
    initialSortOrder: 'asc',
  });

  const { permissions } = useUserRequestsPermissions();
  const canWriteRegistration = !!permissions.registration.write;

  const {
    data: registrationRequests,
    isLoading,
    isFetching,
    isError,
    error,
  } = useRegistration({
    limit: pageSize,
    page: pageIndex,
    filters,
    sortField,
    sortOrder,
  });

  const rows = useMemo(
    () => registrationRequests?.elements || [],
    [registrationRequests],
  );
  const totalRows = registrationRequests?.total || 0;

  useEffect(() => {
    if (!isError) return;

    enqueueSnackbar(
      getApiErrorMessage(error, 'Failed to load registration requests'),
      {
        variant: 'error',
      },
    );
  }, [enqueueSnackbar, error, isError]);

  const handleApprove = async (id: string) => {
    try {
      await updateUserRequestStatus({
        userId: id,
        status: RegistrationRequestStatusEnum.APPROVED,
      });
    } catch (mutationError) {
      enqueueSnackbar(
        getApiErrorMessage(mutationError, 'Failed to approve request'),
        {
          variant: 'error',
        },
      );
    }
  };
  const handleDecline = async (id: string) => {
    try {
      await updateUserRequestStatus({
        userId: id,
        status: RegistrationRequestStatusEnum.DECLINED,
      });
    } catch (mutationError) {
      enqueueSnackbar(
        getApiErrorMessage(mutationError, 'Failed to decline request'),
        {
          variant: 'error',
        },
      );
    }
  };

  const renderToolbar = useCallback(
    (props: ToolbarPropsOverrides) => (
      <UserRequestsToolbar
        {...props}
        total={totalRows}
        label="Registration Requests"
      />
    ),
    [totalRows],
  );

  return (
    <>
      <StyledDataGrid
        rows={rows}
        columns={buildRegistrationColumns(
          handleApprove,
          handleDecline,
          canWriteRegistration,
          formatDate,
        )}
        loading={isLoading || isFetching}
        pageSizeOptions={[10, 25, 50]}
        paginationMode="server"
        filterMode="server"
        sortingMode="server"
        paginationModel={paginationModel}
        onPaginationModelChange={handlePagination}
        sortModel={sortModel}
        onSortModelChange={handleSortModelChange}
        rowCount={totalRows}
        filterModel={filterModel}
        onFilterModelChange={handleFilterModelChange}
        slots={{ toolbar: renderToolbar }}
        showToolbar
        disableRowSelectionOnClick
        onRowClick={params =>
          navigate('/user-requests/registration/details', {
            state: params.row,
          })
        }
        getRowClassName={() => 'clickable'}
      />
    </>
  );
};

export default RegistrationRequests;
